import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

// Per-page header actions (#1054). A page publishes what belongs in the header's
// ⋮ menu; a page that publishes nothing gets no button at all, not a disabled
// one. The registry lives in the shell rather than in each page because the
// header renders above `children` and has no other way to see into them.

export type HeaderAction = {
  id: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  /** Explains a disabled action; rendered as title text. */
  title?: string;
  danger?: boolean;
  /**
   * A count worth seeing WITHOUT opening the menu (#1095, #1013).
   *
   * Rendered on the item and also summed onto the ⋮ trigger, because the point
   * of a badge is that nobody discovers "23 of 24 rooms are unlinked" by
   * opening a dialog to look. A badge that only appeared once the menu was
   * open would be a decoration; on the trigger it is the notification.
   */
  badge?: number;
};

/** What the ⋮ trigger shows: the whole page's outstanding count, or nothing. */
export function headerActionsBadgeTotal(actions: readonly HeaderAction[]): number {
  return actions.reduce((total, action) => total + (action.badge ?? 0), 0);
}

/**
 * Do two registrations look the same to a user?
 *
 * `onSelect` is deliberately NOT compared. It is a closure over page state, so
 * an un-memoized caller hands us a fresh one every render and comparing it
 * would make this guard useless. The provider therefore keeps the newest array
 * in a ref and invokes through that, so skipping the state write can never
 * leave a menu item calling a stale closure — see `HeaderActionsProvider`.
 *
 * Exported for unit testing: the web harness is node-env `renderToStaticMarkup`
 * with no jsdom, so a pure helper is the only part of this file it can reach.
 */
export function sameHeaderActions(a: readonly HeaderAction[], b: readonly HeaderAction[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((left, index) => {
    const right = b[index];
    if (!right) return false;
    return left.id === right.id
      && left.label === right.label
      && left.title === right.title
      && left.icon === right.icon
      && (left.badge ?? 0) === (right.badge ?? 0)
      && Boolean(left.disabled) === Boolean(right.disabled)
      && Boolean(left.danger) === Boolean(right.danger);
  });
}

type HeaderActionsContextValue = {
  /** What the header renders. Only changes when something visible changed. */
  actions: HeaderAction[];
  register: (actions: HeaderAction[]) => void;
  clear: () => void;
  /** Runs the newest `onSelect` for an id, never the one that was rendered. */
  invoke: (id: string) => void;
};

const HeaderActionsContext = createContext<HeaderActionsContextValue>({
  actions: [],
  register: () => undefined,
  clear: () => undefined,
  invoke: () => undefined
});

export function HeaderActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<HeaderAction[]>([]);
  const latest = useRef<HeaderAction[]>([]);

  // The array we RENDER and the array we CALL are deliberately different.
  // Writing state on every registration would loop for an un-memoized caller
  // (new identity → state write → re-render → new identity …), but simply
  // skipping the write would leave us holding whatever closures happened to be
  // current at the last visible change. So: the ref always takes the newest
  // array, and the state write bails out when nothing visible moved. Returning
  // `current` from the updater is what actually stops the render.
  const register = useCallback((next: HeaderAction[]) => {
    latest.current = next;
    setActions((current) => (sameHeaderActions(current, next) ? current : next));
  }, []);

  const clear = useCallback(() => {
    latest.current = [];
    setActions((current) => (current.length === 0 ? current : []));
  }, []);

  const invoke = useCallback((id: string) => {
    const action = latest.current.find((item) => item.id === id);
    if (!action || action.disabled) return;
    action.onSelect();
  }, []);

  const value = useMemo(
    () => ({ actions, register, clear, invoke }),
    [actions, register, clear, invoke]
  );

  return <HeaderActionsContext.Provider value={value}>{children}</HeaderActionsContext.Provider>;
}

/** Read by the header. Pages use {@link useHeaderActions} instead. */
export function useHeaderActionsRegistry(): HeaderActionsContextValue {
  return useContext(HeaderActionsContext);
}

/**
 * Publish this page's header actions. Callers *should* memoize the array, but
 * correctness must not depend on their doing so — the guard in `register` is
 * the safety net, not the caller's discipline.
 */
export function useHeaderActions(actions: HeaderAction[]): void {
  const { register, clear } = useHeaderActionsRegistry();

  // Intentionally no dependency array: this runs after every render of the
  // calling page so the ref keeps the freshest handlers. It is cheap — a ref
  // write plus a comparison that almost always bails out.
  //
  // LAYOUT-timed, not passive. A passive effect runs after the browser can
  // already dispatch a click, and the whole point of the design is that a
  // visibly identical registration does NOT write state — so during that
  // window the menu is rendered, interactive, and `invoke` would still read
  // the previous render's closure out of the ref. Running before paint means
  // the handlers are current by the time anything can be clicked.
  useLayoutEffect(() => {
    register(actions);
  });

  // Separate effect so the cleanup fires only on unmount, not between every
  // render. Clearing on each pass would blank the menu and re-fill it on every
  // keystroke of a page that re-renders while typing. Effect-based rather than
  // render-based so a StrictMode double-mount re-registers rather than
  // leaving the header empty.
  useEffect(() => clear, [clear]);
}
